import { useState } from 'react';
import { agrogestao } from "@/api/agrogestaoClient.js";
import { toast } from "sonner";

export function useLicensePayment() {
  const [pixLoading, setPixLoading] = useState(false);
  const [pixError, setPixError] = useState("");
  const [pixData, setPixData] = useState(null);
  const [pixId, setPixId] = useState("");

  const gerarPix = async ({ propriedadeId, valor, customer }) => {
    setPixError("");
    setPixLoading(true);
    setPixData(null);
    try {
      if (!propriedadeId) throw new Error("Selecione a propriedade");
      if (!customer.name || !customer.email || !customer.phone || !customer.taxId) {
        throw new Error("Informe nome, e-mail, telefone e CPF/CNPJ");
      }

      const description = `Licença AgroGestão - propriedade ${propriedadeId}`;
      const payload = {
        amount: valor,
        expiresIn: 3600,
        description,
        propriedade_id: propriedadeId,
        plano: 'mensal',
        customer: {
          name: customer.name,
          cellphone: customer.phone,
          email: customer.email,
          taxId: customer.taxId
        }
      };

      const response = await agrogestao.functions.invoke('criarQRCodePixLicenca', payload);
      const d = response?.data;
      if (d?.success === false) throw new Error(d?.error || "Falha ao criar QRCode Pix");
      
      const result = d?.data || d;
      setPixData(result);
      const id = result?.id || "";
      setPixId(id);
      
      if (id) {
        const key = `pix_charge_${propriedadeId}`;
        try { localStorage.setItem(key, id); } catch {}
      }
      return result;
    } catch (err) {
      setPixError(err?.message || "Erro ao gerar PIX");
      toast.error(err?.message || "Erro ao gerar PIX");
      throw err;
    } finally {
      setPixLoading(false);
    }
  };

  const verificarPagamento = async (propriedadeId, currentPixId = null) => {
    setPixError("");
    if (!propriedadeId) { 
      setPixError("Selecione a propriedade"); 
      return false; 
    }
    
    let targetId = currentPixId || pixId;
    if (!targetId) {
       try { targetId = localStorage.getItem(`pix_charge_${propriedadeId}`) || ""; } catch {}
    }
    
    if (!targetId) { 
      setPixError("Nenhum pagamento PIX gerado"); 
      return false; 
    }

    try {
      let attempt = 0;
      while (attempt < 2) {
        const res = await agrogestao.functions.invoke('consultarStatusPixLicenca', { 
          propriedade_id: propriedadeId, 
          pix_id: targetId, 
          plano: 'mensal' 
        });
        const d = res?.data;
        const status = d?.status;
        const activated = !!d?.activated || status === 'PAID' || status === 'paid' || status === 'CONFIRMED';
        
        if (activated) {
          return true;
        }

        if (d?.success === false && /not\s*found|404/i.test(String(d?.error || ''))) {
          // Try to find latest invoice if current pix id not found (maybe expired/replaced)
          const { data: fRes } = await agrogestao.client
            .from('faturas')
            .select('pix_id,status,created_date')
            .eq('propriedade_id', propriedadeId)
            .not('pix_id', 'is', null)
            .order('created_date', { ascending: false })
            .limit(1);
            
          const nextId = Array.isArray(fRes) && fRes[0]?.pix_id;
          if (nextId && nextId !== targetId) { 
            targetId = nextId; 
            attempt++; 
            continue; 
          }
          
          setPixError("Pagamento PIX não encontrado. Gere um novo QRCode.");
          return false;
        }

        if (d?.success === false) {
          setPixError(String(d?.error || 'Falha ao consultar status'));
          return false;
        }

        setPixError(`Status atual: ${String(status || 'desconhecido')}`);
        return false;
      }
    } catch (err) {
      // Fallback: check license status directly
      try {
        const list = await agrogestao.entities.Licenca.filter({ propriedade_id: propriedadeId }, '-valid_until', 1);
        const lic = list && list[0];
        const ok = !!lic && (lic.status !== 'bloqueado' && (!lic.valid_until || new Date(lic.valid_until) >= new Date()));
        if (ok) return true;
      } catch {}
      
      setPixError("Serviço de verificação indisponível.");
      return false;
    }
    return false;
  };

  return {
    pixLoading,
    pixError,
    pixData,
    pixId,
    gerarPix,
    verificarPagamento,
    setPixError
  };
}
